import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import moment from 'moment';
import clsx from 'clsx';
import { 
	Checkbox,
	FormControl,
	FormControlLabel, 
	FormGroup, 
	FormHelperText,
	Grid,
	Radio, 
	RadioGroup, 
	TextField, 
	Typography,
	makeStyles,
} from '@material-ui/core';
import { KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers';
import capitalizeFirstLetter from 'utils/capitalizeFirstLetter';

const useStyles = makeStyles(theme => ({
	boldFontWeight: {
		fontWeight: 'bold',
	},
	checkboxSection: {
		display: 'flex',
		flexDirection: 'row',
	},
	commonSpacing: {
		marginBottom: theme.spacing(3),
	},
	container: {
		wordBreak: 'break-word',
	},
	formGroup: {
		marginBottom: theme.spacing(2),
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		wordWrap: 'break-word',
	},
	helpText: {
		marginLeft: theme.spacing(2),
		fontStyle: 'italic',
	},
	inputField: {
		width: '30%',
		[theme.breakpoints.down('md')]: {
			width: '50%',
		},
		[theme.breakpoints.down('xs')]: {
			width: '100%',
		},
	},
	paragraphField: {
		width: '50%',
		[theme.breakpoints.down('md')]: {
			width: '100%',
		},
	},
	selection: {
		display: 'block',
	},
}));

const CustomField = ({ 
	customFields,
	errors, 
	handleBlur, 
	handleChange,
	isLabelWithHelpText,
	isParseOptions,
	isRow,
	setFieldTouched,
	setFieldValue,
	touched, 
	values, 
}) => {
	const classes = useStyles();

	const handleDateChange = (value, name) => {
		if (value) {
			setFieldValue(name, moment(value).format('YYYY-MM-DD').toString())
		} else {
			setFieldValue(name, null);
		}
	};

	const handleTimeChange = (value, name) => {
		if (value) {
			setFieldValue(name, value);
		} else {
			setFieldValue(name, null);
		}
	};

	const handleCheckboxChange = (event, option, name) => {
		const optionsSelected = values?.[name];
        
		if (!touched?.[name]) {
			setFieldTouched(name, true, true);
		}

		if (event.target.checked) {
			setFieldValue(name, optionsSelected?.length === 0 || optionsSelected === undefined 
				? [ option ]
				: [ ...optionsSelected, option ]
			);
		} else {
			setFieldValue(name, [ ...optionsSelected?.filter(selected => selected !== option) ], true) ;
		}
	};

	const renderCustomField = field => {
		const fieldName = field?.fieldName;

		switch (field?.inputType) {
			case 'text': {
				const isSingleLine = field?.maxLength === 100;

				return (
					<TextField
						variant="outlined"
						name={fieldName}
						className={isSingleLine 
							? clsx(classes.inputField, classes.commonSpacing)
							: clsx(classes.paragraphField, classes.commonSpacing)}
						onChange={e => capitalizeFirstLetter(e, setFieldValue, values?.[fieldName])}
						rows={isSingleLine ? 1 : 4}
						fullWidth
						multiline={!isSingleLine}
						onBlur={handleBlur}
						error={errors?.[fieldName] && touched?.[fieldName]}
						helperText={errors?.[fieldName] && touched?.[fieldName]
							? errors?.[fieldName] 
							: !isLabelWithHelpText ? field?.helpText : ''
						}
						value={values?.[fieldName]}
					/>
				)
			}

			case 'date': 
				return (
					<FormControl className={clsx(classes.inputField, classes.commonSpacing)}>
						<Field as={KeyboardDatePicker}
							id={fieldName}
							name={fieldName}
							inputVariant="outlined"
							format="MM/DD/YYYY"
							value={values?.[fieldName]}
							onChange={(value) => handleDateChange(value, fieldName)}
							onBlue={handleBlur}
							KeyboardButtonProps={{
								'aria-label': 'change date',
							}}
							variant="inline"
							clear
							error={errors?.[fieldName] && touched?.[fieldName]}
							helperText={errors?.[fieldName] && touched?.[fieldName]
								? errors?.[fieldName] 
								: !isLabelWithHelpText ? field?.helpText : ''
							}
						/>
					</FormControl>
				)
            
			case 'time': 
				return (
					<FormControl className={clsx(classes.inputField, classes.commonSpacing)}>
						<Field as={KeyboardTimePicker}
							id={fieldName}
							name={fieldName}
							inputVariant="outlined"
							placeholder="08:00 AM"
							mask="__:__ _M"
							value={values?.[fieldName]}
							onChange={value => handleTimeChange(value, fieldName)}
							onBlue={handleBlur}
							variant="inline"
							defaultValue={null}
							clearable
							error={errors?.[fieldName] && touched?.[fieldName]}
							helperText={errors?.[fieldName] && touched?.[fieldName] 
								? errors?.[fieldName] 
								: !isLabelWithHelpText ? field?.helpText : ''
							}
						/>
					</FormControl>
				)
			case 'radio': {
				const options = isParseOptions ? JSON.parse(field?.value) : field?.value;
				const isManyOptions = field?.value?.length > 10;

				return (
					<FormControl className={clsx(classes.selection, classes.formGroup)}>
						<RadioGroup
							aria-labelledby="radio-buttons-group"
							name={fieldName}
							value={values?.[fieldName]}
							onChange={handleChange}
							row={isRow}
						>
							{isRow 
								? options?.map(option => <FormControlLabel key={option} value={option} control={<Radio />} label={option} />)
								: <Grid container className={classes.container}>
									{ options?.map(option =>(
										<Grid key={option} item sm={isManyOptions ? 6 : 12} xs={12}>
											<FormControlLabel value={option} control={<Radio />} label={option} />
										</Grid>
									))
									}
								</Grid>
							}
						</RadioGroup>
						{((errors?.[fieldName] && touched?.[fieldName]) || (field?.helpText && !isLabelWithHelpText))
                        && <FormHelperText
                        	error={!!errors?.[fieldName]}
                        	variant="outlined"
                        >
                        	{errors?.[fieldName] && touched?.[fieldName] ? errors?.[fieldName] : !isLabelWithHelpText ? field?.helpText : ''}
                        </FormHelperText>
						}
					</FormControl>
				)}
			case 'checkbox': {
				const options = isParseOptions ? JSON.parse(field?.value) : field?.value;
				const isManyOptions = field?.value?.length > 10;

				return (
					<FormControl className={clsx(classes.selection, classes.formGroup)}>
						<FormGroup className={clsx(isRow ? classes.checkboxSection : undefined)}>
							{ isRow 
								? options?.map(option =>(<FormControlLabel
									control={
										<Checkbox 
											onBlur={handleBlur} 
											checked={values?.[fieldName]?.includes(option)} 
											onChange={event => handleCheckboxChange(event, option, fieldName)} name={option} 
										/>
									}
									label={option}
									key={option}
								/>))
								: <Grid container className={classes.container}>
									{ options?.map(option =>(
										<Grid key={option} item sm={isManyOptions ? 6 : 12} xs={12}>
											<FormControlLabel
												control={
													<Checkbox 
														onBlur={handleBlur} 
														checked={values?.[fieldName]?.includes(option)} 
														onChange={event => handleCheckboxChange(event, option, fieldName)} name={option} 
													/>
												}
												label={option}
											/>
										</Grid>
									))
									}
								</Grid>
							}
						</FormGroup>
						{((errors?.[fieldName] && touched?.[fieldName]) || (field?.helpText && !isLabelWithHelpText)) 
                        && <FormHelperText
                        	error={!!(errors?.[fieldName] && touched?.[fieldName])}
                        	variant="outlined"
                        >
                        	{(errors?.[fieldName] && touched?.[fieldName]) ? errors?.[fieldName] : !isLabelWithHelpText ? field?.helpText : ''}
                        </FormHelperText>
						}
					</FormControl>
				)}
			default: return;
		}
	}

	return (
		<>
			{customFields?.map((field, index) => (
				<div key={index}>
					<div className={classes.header}>
						<Typography className={classes.boldFontWeight} gutterBottom variant="subtitle1">
							{field?.name}
						</Typography>
						{isLabelWithHelpText && field?.helpText &&
                            <Typography  
                                variant="body1" 
                                gutterBottom
                                className={classes.helpText}
                            >
                                {field?.helpText}
                            </Typography>
						}
					</div>
					{renderCustomField(field)}
				</div>
			))}
		</>
	);
};

CustomField.defaultProps = {
	isLabelWithHelpText: false,
	isParseOptions: true,
	isRow: false,
}

CustomField.propTypes = {
	customFields: PropTypes.object, 
	errors: PropTypes.object, 
	handleBlur: PropTypes.func, 
	handleChange: PropTypes.func, 
	isLabelWithHelpText: PropTypes.bool,
	isParseOptions: PropTypes.bool,
	isRow: PropTypes.bool,
	setFieldTouched: PropTypes.func,
	setFieldValue: PropTypes.func, 
	touched: PropTypes.object, 
	values: PropTypes.object, 
};

export default CustomField;